import { Editor } from '@craftjs/core';
import { Form } from '@weave/schema-gen-ts/dist/schemas/messaging/auto-rules/v1/models.pb';
import { TemplateType_Slug } from '@weave/schema-gen-ts/dist/schemas/messaging/templator/v2/model.pb';
import { theme } from '@frontend/theme';
import { DynamicFieldAttributes, mergeClasses, SharedDynamicFieldsProps } from '@frontend/design-system';
import { COMPOSER_BREAKPOINTS } from './constants';
import { ContentComposerBody } from './content-composer-body/content-composer-body';
import { Designer } from './content-composer-body/designer';
import { ContentComposerFooter } from './content-composer-footer/content-composer-footer';
import { ContentComposerHeader } from './content-composer-header/content-composer-header';
import { ContentComposerProvider } from './content-composer-provider';
import { ComposerDocument, ComposerParts, ComposerMode } from './types';

type Props = {
  children?: React.ReactNode;
  className?: string;
  composerMode: ComposerMode;
  enableValidation?: boolean;
  form?: Form;
  initialSelectedLocationId?: string;
  initialValue?: ComposerDocument;
  locationIds: string[];
  parts: ComposerParts;
  renderMode?: string;
  setForm?: (form: Form) => void;
  sharedDynamicFieldProps?: SharedDynamicFieldsProps;
  subjectDynamicFields?: DynamicFieldAttributes[];
  templateTypeSlug: TemplateType_Slug;
  textDynamicFields?: DynamicFieldAttributes[];
  trackingIdPrefix: string;
};

const SETTINGS_MODAL_HEIGHT_OFFSET = 27; // The gap between the top of the page and the settings modal

export const ContentComposer = ({ parts, children, className, ...rest }: Props) => {
  return (
    <article
      className={mergeClasses('content-composer', className)}
      css={{
        display: 'grid',
        gridTemplateAreas: `'header header' 'sidebar main' 'footer footer'`,
        gridTemplateColumns: 'minmax(200px, 350px) minmax(300px, auto)',
        gridTemplateRows: 'auto minmax(0, 1fr) auto',
        height: '100%',
        position: 'relative',

        [`@media (max-width: ${COMPOSER_BREAKPOINTS.DESKTOP}px)`]: {
          overflow: 'hidden',
          gridTemplateAreas: `'header header' 'main main' 'footer footer'`,
        },

        // Adjust the drag and drop indicator when ContentComposer is used inside the settings modal
        '[data-settings-modal-content] &': {
          '.composer-indicator': {
            marginTop: 0 - SETTINGS_MODAL_HEIGHT_OFFSET,
            [`@media (max-width: ${COMPOSER_BREAKPOINTS.DESKTOP}px)`]: {
              marginTop: 0, // In mobile view, the settings modal is full screen so no need to adjust
            },
          },
        },
      }}
    >
      <Editor
        indicator={{
          className: 'composer-indicator',
          error: theme.colors.status.criticalHover,
          success: theme.colors.primary50,
          thickness: 4,
        }}
        resolver={{ ...parts, Designer }}
      >
        <ContentComposerProvider parts={parts} {...rest}>
          {children}
        </ContentComposerProvider>
      </Editor>
    </article>
  );
};

ContentComposer.Body = ContentComposerBody;
ContentComposer.Header = ContentComposerHeader;
ContentComposer.Footer = ContentComposerFooter;
